import React, { useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { SidebarToggleButton } from './SidebarToggleButton';
import { HeaderPageActionsSlot } from './HeaderPageActions';
import { useHeaderActionsFit } from '../hooks/use-header-actions-fit';
import { APP_HEADER_HEIGHT } from '../constants/layout';

interface ViewHeaderProps {
  children: React.ReactNode;
  centerContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  pageActions?: React.ReactNode;
  marginBottom?: string;
  showSidebarToggle?: boolean;
}

const titleRowActionsSlotClassName = 'flex flex-nowrap items-center justify-end gap-2 empty:hidden';
const secondBarActionsSlotClassName =
  'flex w-full flex-wrap items-center justify-end gap-2 border-t border-b border-border px-4 py-2 empty:hidden';

export const ViewHeader: React.FC<ViewHeaderProps> = ({
  children,
  centerContent,
  rightContent,
  pageActions,
  marginBottom = '2rem',
  showSidebarToggle = false,
}) => {
  const isMobile = useIsMobile();
  const [titleRow, setTitleRow] = useState<HTMLElement | null>(null);
  const [titleCluster, setTitleCluster] = useState<HTMLElement | null>(null);
  const [actionsElement, setActionsElement] = useState<HTMLElement | null>(null);
  const [chrome, setChrome] = useState<HTMLElement | null>(null);
  const fitsInTitleRow = useHeaderActionsFit({
    enabled: isMobile,
    row: titleRow,
    title: titleCluster,
    actions: actionsElement,
    chrome,
  });
  const showActionsInTitleRow = !isMobile || fitsInTitleRow;

  const actionsSlot = (
    <HeaderPageActionsSlot
      className={showActionsInTitleRow ? titleRowActionsSlotClassName : secondBarActionsSlotClassName}
      onSlotElement={setActionsElement}
    >
      {pageActions}
    </HeaderPageActionsSlot>
  );

  return (
    <header
      className={
        centerContent
          ? 'bg-card text-foreground w-full max-w-full min-w-0 overflow-visible'
          : 'bg-card text-foreground w-full max-w-full min-w-0 overflow-hidden'
      }
      style={{
        position: 'sticky',
        top: 0,
        flexShrink: 0,
        boxSizing: 'border-box',
        zIndex: 20,
        marginBottom,
      }}
    >
      <div
        ref={setTitleRow}
        data-testid="app-header-title-row"
        className={showActionsInTitleRow ? 'border-b border-border' : undefined}
        style={{
          position: 'relative',
          height: APP_HEADER_HEIGHT,
          padding: isMobile ? '0 1rem' : '0 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          minWidth: 0,
          overflow: centerContent ? 'visible' : 'hidden',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          ref={setTitleCluster}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            zIndex: 1,
          }}
        >
          {showSidebarToggle && <SidebarToggleButton />}
          {children}
        </div>
        {centerContent != null ? (
          <div
            data-testid="app-header-center"
            className="pointer-events-none absolute inset-x-0 top-0 bottom-0 z-[2] flex items-center justify-center px-24"
          >
            <div className="pointer-events-auto w-full max-w-md">
              {centerContent}
            </div>
          </div>
        ) : null}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto', zIndex: 1 }}>
          {showActionsInTitleRow ? actionsSlot : null}
          {rightContent != null ? (
            <div ref={setChrome} style={{ display: 'flex', alignItems: 'center' }}>
              {rightContent}
            </div>
          ) : null}
        </div>
      </div>
      {!showActionsInTitleRow ? actionsSlot : null}
    </header>
  );
};
